var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import styles from './textarea.styles';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { classMap } from 'lit/directives/class-map.js';
let PTextarea = class PTextarea extends LitElement {
    constructor() {
        super(...arguments);
        this.size = 'normal';
        this.id = '';
        this.value = '';
        this.required = false;
        this.readonly = false;
        this.labelHidden = false;
        this.rows = 4;
        this.labelLeft = false;
    }
    static { this.styles = styles; }
    /**
     * Handle change
     * @returns {void}
    */
    handleChange() {
        this.value = this.input.value;
        this.dispatchEvent(new CustomEvent('p-change', { bubbles: true, composed: true }));
    }
    /**
     * Handle blur
     * @returns {void}
    */
    handleBlur() {
        this.dispatchEvent(new CustomEvent('p-blur', { bubbles: true, composed: true }));
    }
    /**
     * Handle focus
     * @returns {void}
    */
    handleFocus() {
        this.dispatchEvent(new CustomEvent('p-focus', { bubbles: true, composed: true }));
    }
    /**
     * Set focus
     * @returns {void}
    */
    setFocus() {
        this.input.focus();
    }
    /**
     * Handle input
     * @returns {void}
    */
    handleInput() {
        this.value = this.input.value;
        this.dispatchEvent(new CustomEvent('p-input', { bubbles: true, composed: true }));
    }
    /**
     * Print aria-labelledby
     * @returns {string|null} Returns aria-labelledby or null
    */
    printAriaLabelledBy() {
        let ariaLabelBy = null;
        if (this.error) {
            ariaLabelBy = `error-${this.id}`;
        }
        if (this.help) {
            ariaLabelBy = `${ariaLabelBy ? ariaLabelBy + ' ' : ''}help-${this.id}`;
        }
        return ariaLabelBy;
    }
    /**
    * Render component
    * @returns {TemplateResult} Template
    */
    render() {
        const classes = {
            [`${this.size}`]: true,
            ['has-error']: this.error,
            ['label-hidden']: this.labelHidden,
            ['label-left']: this.labelLeft,
        };
        return html ` <div class="input-wrap${classMap(classes)}"> <div class="label-wrap"> <label for=${this.id} class="label${this.labelHidden ? ' sr-only' : ''}"> ${this.label} ${this.required ? html `<span aria-hidden="true">*</span>` : ''}
          </label>
          ${this.help ? html `<div class="help" id="help-${this.id}">${this.help}</div>` : ''}
        </div>
        <div class="input-inner">
          <textarea
            class="input"
            aria-labelledby=${ifDefined(this.printAriaLabelledBy())}
            id=${this.id}
            name=${this.id}
            .value=${live(this.value)}
            placeholder=${ifDefined(this.placeholder)}
            ?required=${this.required}
            aria-invalid=${ifDefined(this.error ? 'true' : null)}
            maxlength=${ifDefined(this.maxlength)}
            rows=${this.rows}
            ?readonly=${this.readonly}
            @change=${this.handleChange}
            @blur=${this.handleBlur}
            @focus=${this.handleFocus}
            @input=${this.handleInput}
          ></textarea>
          ${this.error ? html `<div class="error" id="error-${this.id}">${this.error}</div>` : ''}
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], PTextarea.prototype, "label", void 0);
__decorate([
    property({ type: String })
], PTextarea.prototype, "size", void 0);
__decorate([
    property({ type: String })
], PTextarea.prototype, "help", void 0);
__decorate([
    property({ type: String, reflect: true })
], PTextarea.prototype, "error", void 0);
__decorate([
    property({ type: String, attribute: 'input-id' })
], PTextarea.prototype, "id", void 0);
__decorate([
    property({ reflect: true })
], PTextarea.prototype, "value", void 0);
__decorate([
    property({ type: String })
], PTextarea.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PTextarea.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PTextarea.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean, attribute: 'label-hidden' })
], PTextarea.prototype, "labelHidden", void 0);
__decorate([
    property({ type: Number, reflect: true })
], PTextarea.prototype, "maxlength", void 0);
__decorate([
    property({ type: Number })
], PTextarea.prototype, "rows", void 0);
__decorate([
    property({ type: Boolean, attribute: 'label-left' })
], PTextarea.prototype, "labelLeft", void 0);
__decorate([
    query('.input')
], PTextarea.prototype, "input", void 0);
PTextarea = __decorate([
    customElement('p-textarea')
    /**
    * PTextarea component
    *
    * @class PTextarea
    * @typedef {PTextarea}
    * @extends {LitElement}
    */
], PTextarea);
export { PTextarea };
